// Production error monitoring: forwards uncaught browser errors, promise
// rejections and router errors to /api/public/monitoring/errors, where they are
// logged server-side. Build source maps are emitted so the stacks resolve back
// to original sources.

const ENDPOINT = "/api/public/monitoring/errors";
const MAX_REPORTS_PER_SESSION = 25;
const DEDUPE_WINDOW_MS = 10_000;

type ReportKind = "error" | "unhandledrejection" | "react_error_boundary" | "route_error";

const recentlySent = new Map<string, number>();
let sentCount = 0;
let installed = false;

export function reportClientError(
  error: unknown,
  kind: ReportKind = "error",
  extra: { route?: string } = {},
) {
  if (typeof window === "undefined") return;
  if (sentCount >= MAX_REPORTS_PER_SESSION) return;

  const message =
    error instanceof Error ? error.message : typeof error === "string" ? error : String(error);
  const stack = error instanceof Error ? error.stack : undefined;

  const key = `${kind}:${message}`;
  const now = Date.now();
  const last = recentlySent.get(key);
  if (last !== undefined && now - last < DEDUPE_WINDOW_MS) return;
  recentlySent.set(key, now);
  sentCount += 1;

  const payload = JSON.stringify({
    kind,
    message: message.slice(0, 1000),
    ...(stack ? { stack: stack.slice(0, 8000) } : {}),
    url: window.location.href.slice(0, 2000),
    route: extra.route ?? window.location.pathname,
    userAgent: navigator.userAgent.slice(0, 500),
    release: import.meta.env["VITE_APP_RELEASE"] ?? "dev",
  });

  try {
    if (navigator.sendBeacon) {
      navigator.sendBeacon(ENDPOINT, new Blob([payload], { type: "application/json" }));
      return;
    }
  } catch {
    // fall through to fetch
  }
  void fetch(ENDPOINT, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: payload,
    keepalive: true,
  }).catch(() => undefined);
}

export function installClientMonitoring() {
  if (installed || typeof window === "undefined") return;
  installed = true;

  window.addEventListener("error", (event) => {
    reportClientError(event.error ?? event.message, "error");
  });
  window.addEventListener("unhandledrejection", (event) => {
    reportClientError(event.reason, "unhandledrejection");
  });
}
